import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Alert, Button, Card, CardActions, IconButton } from '@mui/material';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import ReactPlayer from 'react-player';
import toast from 'react-hot-toast';
import { useMobileApp } from '../../../hooks/useMobileApp';
import { LiveStream } from '../../../interfaces/interfaces';
import { useSelector } from '../../../reduxToolkit/typedRedux';
import { createDisplayLink } from '../../../services/helper';
import { deleteLivestream, updateLivestream } from '../../../services/services';
import { VyCopyField } from '../../common/VyCopyField';
import VyModal from '../../common/VyModal';
import VyTextField from '../../common/VyTextField';
import VyTooltip from '../../common/VyTooltip';
import VyUpload from '../../common/VyUpload';
import { SimulcastComponent } from './SimulcastComponent';
import { LivestreamStatusTags } from './StatusTag';

interface VyUploadModalProps {
  onSubmit: () => void;
  onClose: () => void;
  onUpdate: () => void;
  open?: boolean;
  eventid?: string;
  livestream: LiveStream;
}

const LivestreamModalUpdate = ({ open = false, onClose, onUpdate, livestream }: VyUploadModalProps) => {
  const { t } = useTranslation(['account']);
  const [updated, setUpdated] = useState<boolean>(false);
  const [nameEdit, setNameEdit] = useState<boolean>(false);
  const [name, setName] = useState<string>(livestream.name);
  const [thumbnail, setThumbnail] = useState<string | null>(livestream.thumbnail);
  const [impressionCount, setImpressionCount] = useState<number>();
  const { isAppView } = useMobileApp();

  const currentStreamState = useSelector((state) => state.common.livestreams).find((stream) => stream._id === livestream._id);

  const livestreamIframe = `<iframe style="overflow: hidden;" frameborder="0" src="${process.env.baseURL}/embed/${livestream._id}" width="800" height="450" scrolling="no">Iframes not supported</iframe>`;

  async function updateStream() {
    await updateLivestream(livestream._id, {
      liveStreamId: livestream.liveStreamId,
      name: name || undefined,
      thumbnail: thumbnail,
    });
    onUpdate();
  }

  async function deleteStream() {
    await deleteLivestream(livestream._id);
    onUpdate();
    onClose();
  }

  async function onModalClose() {
    updated && (await updateStream());
    onClose();
  }

  useEffect(() => {
    if (name !== livestream.name || thumbnail !== livestream.thumbnail) setUpdated(true);
  }, [name, thumbnail]);

  return (
    <>
      <VyModal disableDefaultStyling open={open} onClose={onModalClose} maxWidth="80vw">
        {() => (
          <div className="flex flex-col [&>label]:mt-4" style={{ width: '50vw' }}>
            <div className="flex justify-between">
              <div className="flex flex-row content-center place-items-center">
                {livestream.liveStreamType === 'mux' && (
                  <div className="w-4 h-4 rounded-full" style={{ background: currentStreamState?.isBroadcasting ? 'red' : 'grey' }} />
                )}
                {!nameEdit ? (
                  <>
                    <p className="pl-2 text-xl text-slat-800">{name}</p>
                    <IconButton onClick={() => setNameEdit(true)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </>
                ) : (
                  <VyTextField
                    onBlur={(e) => {
                      setName(e.target.value);
                      setNameEdit(false);
                    }}
                    color="secondary"
                    defaultValue={name}
                    fullWidth
                  />
                )}
              </div>
              <div className="flex justify-items-end">
                {impressionCount && impressionCount > 0 ? (
                  <VyTooltip title={t('livestream.impressions-tooltip')}>
                    <div className="flex flex-row content-center space-x-2 place-items-center">
                      <span>{impressionCount}</span>
                      <VisibilityIcon />
                    </div>
                  </VyTooltip>
                ) : (
                  <></>
                )}
              </div>
            </div>
            <ReactPlayer
              playsinline={isAppView()}
              url={livestream.hls}
              width="100%"
              height="400px"
              controls={true}
              light={livestream.thumbnail ? createDisplayLink(livestream.thumbnail) : undefined}
            />
            <LivestreamStatusTags stream={currentStreamState} mode="row" className="mt-3" />
            <label>
              <VyTooltip title={t('account:livestream.stream-key-tip')}>
                <span>{t('account:livestream.stream-key')}</span>
              </VyTooltip>
              <VyCopyField value={livestream.streamKey} successMessage={t('account:copy-success')} />
            </label>
            <label>
              <VyTooltip title="RTMP">
                <span>RTMP</span>
              </VyTooltip>
              <VyCopyField value={livestream.rtmpUrl} successMessage={t('account:copy-success')} />
            </label>

            <SimulcastComponent onUpdate={onUpdate} livestream={livestream} />

            <div>
              <span>Thumbnail</span>
              <VyUpload
                randomExternalImageButton={false}
                defaultFileId={thumbnail}
                onChange={(fileId: string | null) => {
                  setThumbnail(fileId);
                }}
                recommendedAspectRatio="1920 x 1080"
              />
            </div>
            <label>{t('account:iframe-label')}</label>
            <div className="flex mt-2 space-x-3">
              <VyTextField disabled fullWidth aria-readonly color="secondary" value={livestreamIframe} />
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(livestreamIframe);
                  toast.success(t('livestream.copy-success'));
                }}
                variant="outlined"
                color="secondary">
                {t('livestream.copy')}
              </Button>
            </div>
            <div className="mt-4 mb-4">
              <span>{t('livestream.actions')}</span>
              <Card variant="outlined">
                <CardActions>
                  <div>
                    <span>{t('livestream.delete')}</span>
                    <IconButton onClick={() => deleteStream()} size="large">
                      <DeleteIcon htmlColor="#f54254"></DeleteIcon>
                    </IconButton>
                  </div>
                </CardActions>
              </Card>
            </div>
            <Alert severity="info">{t('livestream.recording-info')}</Alert>
          </div>
        )}
      </VyModal>
    </>
  );
};

export default LivestreamModalUpdate;
